import { useState, useEffect, useRef } from 'react'

const SNOOZE_KEY = 'pwa-install-snooze-until'
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000

function isSnoozed() {
  if (typeof localStorage === 'undefined') return false
  const until = Number(localStorage.getItem(SNOOZE_KEY) || 0)
  return until > Date.now()
}

function snooze() {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
}

function detectIOS() {
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const vendor = navigator.vendor || ''

  if (/iPad|iPhone|iPod/i.test(ua)) return true

  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    const isApple = vendor.indexOf('Apple') > -1
    const hasSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
    if (isApple && hasSafari) return true
  }

  if (navigator.maxTouchPoints > 1) {
    const isApple = vendor.indexOf('Apple') > -1
    const hasSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
    if (isApple && hasSafari) return true
  }

  return false
}

function checkInstalled() {
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  if (window.navigator && window.navigator.standalone === true) {
    return true
  }
  return false
}

/**
 * Prompt de instalación PWA:
 * - Chromium/Android: beforeinstallprompt
 * - iOS/Safari: instrucciones manuales
 * - Snooze 7 días (no dismiss permanente)
 */
export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [canNativeInstall, setCanNativeInstall] = useState(false)
  const deferredPromptRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return
    }

    const ios = detectIOS()
    const installed = checkInstalled()
    setIsIOS(ios)
    setIsInstalled(installed)

    if (installed || isSnoozed()) {
      return
    }

    const onBeforeInstall = (event) => {
      event.preventDefault()
      deferredPromptRef.current = event
      setCanNativeInstall(true)
      setTimeout(() => setShowPrompt(true), 1500)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    if (ios) {
      const timer = setTimeout(() => setShowPrompt(true), 2000)
      return () => {
        clearTimeout(timer)
        window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
    }
  }, [])

  const handleSnooze = () => {
    setShowPrompt(false)
    snooze()
  }

  const handleNativeInstall = async () => {
    const promptEvent = deferredPromptRef.current
    if (!promptEvent) {
      handleSnooze()
      return
    }
    try {
      promptEvent.prompt()
      await promptEvent.userChoice
    } catch (_) {
      // ignore
    } finally {
      deferredPromptRef.current = null
      setCanNativeInstall(false)
      setShowPrompt(false)
      snooze()
    }
  }

  if (isInstalled || !showPrompt || isSnoozed()) {
    return null
  }

  // Android/Chromium with native install
  if (!isIOS && canNativeInstall) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-6">
        <div className="mx-auto max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl p-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Instalar Artemis</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Accede más rápido desde tu pantalla de inicio.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleSnooze}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Ahora no
            </button>
            <button
              type="button"
              onClick={handleNativeInstall}
              className="flex-1 rounded-lg bg-brand-500 px-3 py-2.5 text-sm font-semibold text-white"
            >
              Instalar app
            </button>
          </div>
        </div>
      </div>
    )
  }

  // iOS instructions
  if (!isIOS) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-800 rounded-xl flex items-center justify-center">
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Instalar Artemis</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              En Safari: Compartir → Agregar a pantalla de inicio.
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 space-y-3">
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">1.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Toca <strong>Compartir</strong> en la barra de Safari
                </p>
              </div>
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">2.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Elige <strong>Agregar a pantalla de inicio</strong>
                </p>
              </div>
              <div className="flex items-start">
                <span className="font-bold text-primary-600 dark:text-primary-400 mr-3 flex-shrink-0 text-lg">3.</span>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Confirma con <strong>Agregar</strong>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSnooze}
              className="w-full bg-primary-600 text-white px-4 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-base"
            >
              Recordarme en 7 días
            </button>
          </div>
          <button
            type="button"
            onClick={handleSnooze}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Cerrar"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
